import {
  ChangeDetectionStrategy,
  Component,
  input,
  output,
} from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { LoadingButtonDirective } from './loading-button.directive';

@Component({
  selector: 'lib-form-footer-buttons',
  imports: [MatButton, MatIcon, MatIconButton, LoadingButtonDirective],
  template: `
    <div class="button-container">
      <div class="action-buttons">
        @if (confirmButtonLabel() !== null) {
          <button
            mat-flat-button
            class="confirm-button"
            data-testid="confirm_button"
            [disabled]="isConfirmBtnDisabled() || loading()"
            (click)="confirmButtonClick.emit()"
            [loading]="loading()"
          >
            {{ confirmButtonLabel() }}
          </button>
        }

        @if (secondaryButtonLabel() !== null) {
          <button
            mat-stroked-button
            class="secondary-button"
            data-testid="secondary_button"
            (click)="secondaryButtonClick.emit()"
            [attr.color]="secondaryButtonColor()"
            [loading]="secondaryBtnLoading()"
          >
            {{ secondaryButtonLabel() }}
          </button>
        }

        @if (tertiaryButtonLabel()) {
          <button
            mat-stroked-button
            color="error"
            class="tertiary-button"
            data-testid="tertiary_button_with_label"
            (click)="tertiaryButtonClick.emit()"
            [loading]="tertiaryBtnLoading()"
          >
            {{ tertiaryButtonLabel() }}
          </button>
        } @else if (tertiaryButtonIcon()) {
          <button
            mat-icon-button
            color="error"
            data-testid="tertiary_button"
            (click)="tertiaryButtonClick.emit()"
            [loading]="tertiaryBtnLoading()"
          >
            <mat-icon>
              {{ tertiaryButtonIcon() }}
            </mat-icon>
          </button>
        }
      </div>

      @if (clearButtonLabel() !== null) {
        <button
          mat-button
          class="clear-btn"
          data-testid="clear_button"
          (click)="clearButtonClick.emit()"
        >
          {{ clearButtonLabel() }}
        </button>
      }
    </div>
  `,
  styles: `
    @use '@angular/material' as mat;
    @use 'theme' as theme;

    :host {
      display: block;
    }

    .button-container {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 1rem;
    }
    .action-buttons {
      display: flex;
      gap: 0.5rem;
    }

    .clear-btn {
      text-decoration: underline;
    }

    .confirm-button {
      transition: all 0.3s ease;
    }

    .tertiary-button {
      @include mat.button-overrides(
        (
          outlined-outline-color: error,
        )
      );
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormFooterButtonsComponent {
  confirmButtonLabel = input<string | null>('Ok');
  loading = input<boolean>(false);
  isConfirmBtnDisabled = input<boolean>(false);

  secondaryButtonLabel = input<string | null>(null);
  secondaryBtnLoading = input<boolean>(false);
  secondaryButtonColor = input('primary');

  tertiaryButtonIcon = input<string>();
  tertiaryButtonLabel = input<string | null>(null);
  tertiaryBtnLoading = input<boolean>(false);

  clearButtonLabel = input<string | null>('Clear');

  confirmButtonClick = output<void>();
  secondaryButtonClick = output<void>();
  tertiaryButtonClick = output<void>();
  clearButtonClick = output<void>();
}
